import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ActionLink } from '@klp/design';
import {
    FilterCard,
    FilterDatePicker,
    FilterMelding,
    FilterRow,
    FilterSok,
    FilterStack,
    FilterToggle,
} from '@klp/design/FilterPanel/FilterPanel';
import {
    getAktiveStatuser,
    getListParams,
    harAktivtFilter,
} from '@klp/design/FilterPanel/listParams';
import { ListHeader } from '@klp/design/ListHeader/ListHeader';
import { RegionSelector, PortfolioSelector } from '@klp/portfolio/components';
import { usePortfolio, useRegion } from '@klp/portfolio/store';
import { Main } from '@klp/layout';
import { leverandorSelectors, isActiveModal } from '../store/leverandorSlice';
import { LeverandorTable, CreateLeverandor } from '../components';
import {
    useGetLeverandorProcessListQuery,
    useGetLeverandorProcessStatisticsQuery,
    useGetLeverandorStatusQuery,
} from '../services/rtkApi';
import { LeverandorAccess, LeverandorRoutes } from '../config';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { BrukerRoller } from '@klp/user/types';
import { TildelAnsvarlig } from '../components/TildelAnsvarlig/TildelAnsvarlig';
import { SendKommentar } from '../components/SendKommentar/SendKommentar';
import { useCurrentUser } from '@klp/auth';
import { showToast } from '@klpkonsern/ui';
import { formatDateAPI as formatDate } from '@klp/utils/dateTimeFormatter';
import { useListFilters } from '@forvalter/frontpage/hooks/useListFilters';
import { StatusSelector } from '@klp/design/StatusSelector/StatusSelector';

export default function LeverandorListView() {
    const { t, i18n } = useTranslation();
    const { portfolio, resetPortfolioField } = usePortfolio();
    const { region, resetRegionField } = useRegion();
    const { activeModal } = useSelector(leverandorSelectors);
    const { currentUser } = useCurrentUser();

    const {
        filters,
        updateFilter,
        resetFilters,
        fraDato,
        setFraDato,
        setOppslag,
        tildeltMeg,
        setTildeltMeg,
        filtreLaast,
        setSokTekst,
    } = useListFilters('leverandor');

    // How many are assigned to the user, the same count as on the front page
    const { data: statistikk } = useGetLeverandorProcessStatisticsQuery(
        { ansvarlig: currentUser?.epost },
        { skip: !currentUser?.epost },
    );
    const tildeltTil = tildeltMeg ? currentUser?.epost : undefined;

    const {
        data: leverandorStatusList,
        isLoading: leverandorStatusListIsLoading,
    } = useGetLeverandorStatusQuery();

    // «Tildelt meg» is sent alone. Otherwise «Alle» and empty fields are left
    // out of the request, and a search is sent without the filters.
    const filterParams = tildeltTil
        ? { tildeltTil }
        : getListParams(
              {
                  portfolio: portfolio?.brukerId,
                  region: region?.name,
                  statuses: getAktiveStatuser(
                      filters.statuses,
                      leverandorStatusList,
                  ),
                  fromDate: fraDato ? formatDate(fraDato) : undefined,
              },
              { sok: filters.oppslag },
          );
    // The endpoint has no paging, so the list is only fetched when a filter or
    // a search narrows it
    const kanHente = harAktivtFilter(filterParams);

    const {
        data: leverandorData,
        isLoading: leverandorIsLoading,
        isFetching: leverandorIsFetching,
        isError: leverandorIsError,
    } = useGetLeverandorProcessListQuery(
        { ...filterParams, culture: i18n.language },
        { skip: leverandorStatusListIsLoading || !kanHente },
    );

    // RTK Query keeps the last result while the query is skipped, so hide it
    // when nothing narrows the list any more
    const leverandorer = React.useMemo(
        () => (kanHente ? (leverandorData ?? []) : []),
        [leverandorData, kanHente],
    );

    const matchingLeverandor = leverandorer?.find(
        (leverandor) =>
            leverandor.prosessInstansId === activeModal?.prosessInstansId,
    );

    const handleStatusChange = (statuses: Array<string>) => {
        updateFilter('statuses', statuses);
    };

    const handleNullstillFilters = () => {
        resetPortfolioField();
        resetRegionField();
        resetFilters();
    };

    if (leverandorIsError) {
        showToast({ body: t('general.error.message'), type: 'error' });
    }

    return (
        <div>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="leverandorSchema"
                    label={t('breadcrumbs.leverandor.schema')}
                />
            </Main.Breadcrumbs>
            {LeverandorAccess(currentUser) && (
                <>
                    <SendKommentar.Modal />
                    {isActiveModal(activeModal, 'tildelAnsvarlig') &&
                        matchingLeverandor?.currentAktivitet && (
                            <TildelAnsvarlig.Modal
                                superOfficeContactId={
                                    matchingLeverandor.superOfficeContactId
                                }
                                prosessInstansId={
                                    matchingLeverandor.prosessInstansId
                                }
                                kandidatGruppe={
                                    matchingLeverandor.currentAktivitet
                                        .kandidatGruppe
                                }
                                currentAnsvarlig={
                                    matchingLeverandor.currentAktivitet
                                        .tildeltTil
                                }
                                activity={matchingLeverandor.currentAktivitet}
                            />
                        )}

                    <ListHeader title={t('leverandor.heading')}>
                        <ActionLink
                            actionType="link"
                            href={LeverandorRoutes.leverandorSchemaDocumentationUrl()}
                            endIcon={
                                <Illustration
                                    icon={IconName.CIRCLE_INFO}
                                    klpPalette="secondary"
                                />
                            }
                            translation="leverandor.header.documentation"
                        />
                        <ActionLink
                            actionType="link"
                            href={LeverandorRoutes.leverandorInherentRisk()}
                            endIcon={
                                <Illustration
                                    icon={IconName.CIRCLE_OBS_FILLED}
                                    klpPalette="secondary"
                                />
                            }
                            translation="leverandor.inherentRisk.title"
                        />
                        <CreateLeverandor />
                    </ListHeader>

                    <FilterCard
                        loading={
                            kanHente &&
                            (leverandorIsLoading || leverandorIsFetching)
                        }
                        filters={
                            <FilterStack>
                                <FilterSok
                                    id="leverandor-sok"
                                    label={t('leverandor.oppslag.sokLabel')}
                                    value={filters.oppslag}
                                    onChange={setOppslag}
                                    onInputChange={setSokTekst}
                                    disabled={tildeltMeg}
                                    foran={
                                        <FilterToggle
                                            label={t('filterPanel.tildeltMeg')}
                                            antall={statistikk?.TildeltMeg}
                                            aktiv={tildeltMeg}
                                            onChange={setTildeltMeg}
                                        />
                                    }
                                    helperText={
                                        tildeltMeg
                                            ? t('filterPanel.visTildelt')
                                            : filtreLaast
                                              ? t('filterPanel.sokUtenFiltre')
                                              : undefined
                                    }
                                />
                                <FilterRow onReset={handleNullstillFilters}>
                                    <PortfolioSelector
                                        label="filterPanel.portefolje"
                                        allLabel="filterPanel.alle"
                                        filter={[
                                            'isForvalter',
                                            'isOkonomi',
                                            BrukerRoller.Drift,
                                        ]}
                                        disabled={filtreLaast}
                                    />

                                    <RegionSelector
                                        label="filterPanel.region"
                                        allLabel="filterPanel.alle"
                                        disabled={filtreLaast}
                                    />

                                    <StatusSelector
                                        label="filterPanel.status"
                                        allLabel="filterPanel.alle"
                                        statusList={leverandorStatusList}
                                        statusListIsLoading={
                                            leverandorStatusListIsLoading
                                        }
                                        value={filters.statuses}
                                        onChange={handleStatusChange}
                                        translationPrefix="leverandor.statuses"
                                        displayMode="summary"
                                        disabled={filtreLaast}
                                    />

                                    <FilterDatePicker
                                        label={t('filterPanel.endretEtter')}
                                        value={fraDato}
                                        onChange={setFraDato}
                                        clearable={true}
                                        disabled={filtreLaast}
                                    />
                                </FilterRow>
                            </FilterStack>
                        }
                    >
                        {kanHente ? (
                            <LeverandorTable leverandorer={leverandorer} />
                        ) : (
                            <FilterMelding>
                                {t('filterPanel.velgFilter')}
                            </FilterMelding>
                        )}
                    </FilterCard>
                </>
            )}
        </div>
    );
}
